import { get } from 'config/api/config';

import { FloorPlan, Unit, UnitAmenities } from 'types/floorplan-page-types';
import { RoommateInterest } from 'types/roommate-interest';
import { getTodaysDate } from 'utils/date';

const PROPERTIES_ENDPOINT = '/viewer/properties';
const PROPERTY_SETTINGS_ENDPOINT = '/viewer/propertySettings';
const PROPERTY_OPTIONS_ENDPOINT = '/viewer/property';

interface PropertySettingsResult {
  key: string;
  label: string;
  default_value: any;
  selected_value: boolean;
}
/**
 * To GET property settings
 * @returns promise
 */
export const getPropertySettings = async (propertyId: number) => {
  if (!propertyId) throw new Error('Property Id is missing.');

  const response = await get<PropertySettingsResult[]>(
    `${PROPERTY_SETTINGS_ENDPOINT}/${propertyId}`,
  );
  return response;
};

/**
 * To GET floor plans
 * @param {number} propertyId
 * @param {string} moveInDate
 * @param {string} [dateBasedLeaseTermId] - Optional date-based lease term ID for student housing
 * @returns promise
 */
export const getFloorPlans = async (
  propertyId: number,
  moveInDate: string,
  dateBasedLeaseTermId?: string,
  studentParams?: {
    leaseTermIds?: number[];
    leaseStartWindowIds?: number[];
    spaceConfigurationIds?: number[];
  },
) => {
  if (!propertyId || !moveInDate) throw new Error('Missing parameter.');

  const params: any = { moveInDate };
  if (dateBasedLeaseTermId) {
    params.dateBasedLeaseTermId = dateBasedLeaseTermId;
  }
  if (studentParams?.leaseTermIds?.length) {
    params.leaseTermIds = studentParams.leaseTermIds.join(',');
  }
  // An empty array is meaningful for student lease terms with no associated
  // start window: send the parameter explicitly rather than omitting it.
  if (studentParams?.leaseStartWindowIds !== undefined) {
    params.leaseStartWindowIds = studentParams.leaseStartWindowIds.join(',');
  }
  if (studentParams?.spaceConfigurationIds?.length) {
    params.spaceConfigurationIds = studentParams.spaceConfigurationIds.join(',');
  }
  const response = await get<FloorPlan[]>(`${PROPERTIES_ENDPOINT}/${propertyId}/floor-plans`, {
    params,
  });
  return response?.data;
};

/**
 * To GET available units
 * @param {string} floorPlanId
 * @returns promise
 */
export const getAvailableUnitsAndLeaseTerm = async (
  propertyId: number,
  floorPlanId: number,
  moveInDate: string,
  leaseId?: number,
  getOnlyWebVisibleUnits?: boolean,
) => {
  if (!propertyId || !floorPlanId) {
    throw new Error('A parameter is missing while fetching units.');
  }

  // Default to current date if moveInDate is empty
  const effectiveMoveInDate = moveInDate || getTodaysDate();
  const params: any = { moveInDate: effectiveMoveInDate };
  if (leaseId) {
    params.leaseId = leaseId;
  }
  const URL = `${PROPERTIES_ENDPOINT}/${propertyId}/floor-plans/${floorPlanId}/available-units`;
  const response = await get<Unit[]>(URL, { params });

  let units = response?.data || [];

  if (getOnlyWebVisibleUnits === true) {
    units = units.filter((unit) => unit.allowOnWebsite === true);
  }

  return units;
};

/**
 * To GET available units for student housing
 * @param {number} propertyId
 * @param {number} floorPlanId
 * @param {string} moveInDate
 * @param {number} [leaseId] - Optional lease ID
 * @param {boolean} [getOnlyWebVisibleUnits] - Filter for web visible units only
 * @param {number[]} [spaceConfigurationIds] - Array of space configuration IDs
 * @param {number} [leaseTermId] - Lease term ID
 * @param {number} [leaseStartWindowId] - Lease start window ID
 * @returns promise
 */
export const getAvailableUnitsAndLeaseTermStudent = async (
  propertyId: number,
  floorPlanId: number,
  moveInDate: string,
  leaseId?: number,
  getOnlyWebVisibleUnits?: boolean,
  spaceConfigurationIds?: number[],
  leaseTermId?: number,
  leaseStartWindowId?: number,
) => {
  if (!propertyId || !floorPlanId) {
    throw new Error('A parameter is missing while fetching units.');
  }

  // Default to current date if moveInDate is empty
  const effectiveMoveInDate = moveInDate || getTodaysDate();
  const params: any = { moveInDate: effectiveMoveInDate };
  if (leaseId) {
    params.leaseId = leaseId;
  }
  if (spaceConfigurationIds && spaceConfigurationIds.length > 0) {
    params.spaceConfigurationIds = spaceConfigurationIds.join(',');
  }
  if (leaseTermId) {
    params.leaseTermId = leaseTermId;
  }
  if (leaseStartWindowId) {
    params.leaseStartWindowId = leaseStartWindowId;
  }

  const URL = `${PROPERTIES_ENDPOINT}/${propertyId}/floor-plans/${floorPlanId}/available-units`;
  const response = await get<Unit[]>(URL, { params });

  let units = response?.data || [];

  if (getOnlyWebVisibleUnits === true) {
    units = units.filter((unit) => unit.allowOnWebsite === true);
  }

  return units;
};

/**
 * To GET reserved unit
 * @returns promise
 */
export const getReservedUnitForApplicant = async (propertyId: number) => {
  if (!propertyId) throw new Error('Missing parameter.');

  const response = await get<Unit>(`${PROPERTIES_ENDPOINT}/${propertyId}/reserved-unit`);
  return response;
};

/**
 * To GET add-ons
 * @returns promise
 */
export const getAddOns = async (propertyId: number, moveInDate: string) => {
  if (!propertyId || !moveInDate) throw new Error('Missing parameter.');

  const response = await get(`${PROPERTY_OPTIONS_ENDPOINT}/${propertyId}/addons`, {
    params: { moveInDate },
  });
  return response;
};

/**
 * To GET all add-ons
 * @returns promise
 */
export const getAllAddOns = async (
  propertyId: number,
  leaseId: number,
  moveInDate = getTodaysDate(),
) => {
  if (!propertyId || !leaseId) throw new Error('Missing parameter.');

  const response = await get(`${PROPERTY_OPTIONS_ENDPOINT}/${propertyId}/all-addon/${leaseId}`, {
    params: { moveInDate },
  });
  return response;
};

/**
 * To GET amenities
 * @param {string} floorPlanId
 * @returns promise
 */
export const getFloorPlanAmenities = async (propertyId: number, floorPlanId: number) => {
  if (!propertyId || !floorPlanId) throw new Error('Missing parameter.');

  const response = await get<UnitAmenities[]>(
    `${PROPERTIES_ENDPOINT}/${propertyId}/floor-plans/${floorPlanId}/amenities`,
  );
  return response?.data;
};

/**
 * To GET date-based lease terms
 * @param {number} propertyId
 * @param {string} applicationId
 * @returns promise
 */
export const getDateBasedLeaseTerms = async (
  propertyId: number,
  applicationId: string,
  orgId?: number,
) => {
  if (!propertyId || !applicationId) throw new Error('Missing parameter.');

  const response = await get(`${PROPERTIES_ENDPOINT}/${propertyId}/date-based-lease-terms`, {
    params: { applicationId, ...(orgId ? { cid: orgId } : {}) },
  });

  return response?.data;
};

/**
 * Returns the academic-year/lease-term/start-window associations for a property.
 * The API intentionally returns one row per association; callers must not deduplicate it.
 */
export const getAcademicYears = async (propertyId: number, orgId?: number) => {
  if (!propertyId) throw new Error('Missing parameter.');

  const response = await get(`${PROPERTIES_ENDPOINT}/${propertyId}/academic-years`, {
    params: orgId ? { cid: orgId } : undefined,
  });
  return response?.data;
};

/**
 * To GET roommate interests
 * @param {number} propertyId
 * @returns promise
 */
export const getRoommateInterests = async (propertyId: number) => {
  if (!propertyId) throw new Error('Missing parameter.');

  const response = await get<RoommateInterest[]>(
    `${PROPERTIES_ENDPOINT}/${propertyId}/roommate-interests`,
  );

  return response?.data;
};
